<template>
  <div class="plot-form">
    <form @submit.prevent="handleSubmit" @click.stop>
      <AeStepper :steps="steps" mode="controlled" ref="stepperRef">
        <!-- Step 1: Geometria -->
        <template #geometry>
          <div class="geometry-step">

          <!-- Warning per plot con multiple coltivazioni -->
          <AeAlert v-if="entityType === 'plot' && isEditMode && relatedFields.length > 1" type="warning" class="mb-4">
            <p class="helper-text">
              {{ t('plotForm.warningInheritedPart1') }} <strong>{{ t('plotForm.warningInheritedBold') }}</strong> {{ t('plotForm.warningInheritedPart2', { count: relatedFields.length }) }}
            </p>
            <ul class="related-fields-list">
              <li v-for="f in relatedFields" :key="f.id_field">
                <strong>{{ relatedSeasons.find(s => s.id_season === f.id_season)?.name || f.id_season }}</strong>
                — {{ translateCropName(f.crop?.name) || '—' }}
              </li>
            </ul>
          </AeAlert>

          <!-- Nome full width -->
          <AeInput v-model="formData.name" :label="entityType === 'plot' ? t('plotForm.namePlotLabel') : t('plotForm.nameGeoElementLabel')"
            :placeholder="entityType === 'plot' ? t('plotForm.namePlotPlaceholder') : t('plotForm.nameGeoElementPlaceholder')" required
            :error="errors.name" />


            <!-- Geo Element Type (only for geo_element) -->
          <AeSelect v-if="entityType === 'geo_element'" v-model="formData.id_type" :options="geoElementTypeOptions"
            :label="t('plotForm.typeLabel')" :placeholder="t('plotForm.typePlaceholder')" searchable required />

          <PlotMap
            :center-lat="centerLat"
            :center-lon="centerLon"
            :geometry="plotGeometry"
            :other-plots="otherPlots"
            :geom-type="selectedGeomType"
            height="380px"
            @update:geometry="handleGeometryUpdate"
            @update:area="handleAreaUpdate"
          />
          </div>
        </template>

        <!-- Step 2: Dettagli -->
        <template #details>

          <div class="fields-column">

            <!-- {{ allCropOptions }} -->

            <!-- Coltura (unico selettore — derive plot_type automaticamente) -->
            <AeSelect
              v-if="entityType === 'plot'"
              v-model="formData.id_crop"
              :options="allCropOptions"
              :label="t('plotForm.cropLabel')"
              :placeholder="t('plotForm.cropPlaceholder')"
              searchable
              @update:modelValue="onCropChange"
            />

            <!-- Tipo derivato (sola lettura, informativo) -->
            <div v-if="entityType === 'plot' && formData.id_crop" class="derived-type">
              <AeIcon name="Info" :size="13" />
              {{ formData.plot_type === 'orchard' ? t('plotForm.permanentCropType') : t('plotForm.fieldCropType') }}
            </div>

            <!-- Dati di impianto (solo orchard) — temporaneamente nascosto -->
            <AeButton
              v-if="entityType === 'plot' && formData.plot_type === 'orchard'"
              variant="secondary"
              size="small"
              @click="showOrchardModal = true"
            >
              <AeIcon name="Sprout" :size="14" />
              {{ hasOrchardData ? t('plotForm.orchardDataEditButton') : t('plotForm.orchardDataButton') }}
            </AeButton>

            <!-- Area -->
            <AeInput
              v-model.number="formData.area"
              type="number"
              min="0"
              :label="t('plotForm.areaLabel')"
              :placeholder="t('plotForm.areaPlaceholder')"
              :error="errors.area"
            />

            <!-- Note / Description -->
            <AeTextarea
              v-if="entityType === 'plot'"
              v-model="formData.note"
              :label="t('plotForm.noteLabel')"
              :placeholder="t('plotForm.notePlaceholder')"
              :rows="3"
            />

            <AeTextarea
              v-else
              v-model="formData.description"
              :label="t('plotForm.descriptionLabel')"
              :placeholder="t('plotForm.descriptionPlaceholder')"
              :rows="3"
            />

          </div>
        </template>
      </AeStepper>
    </form>

    <AeModal v-model="showErrorModal" :title="t('plotForm.missingDataModalTitle')" size="small">
      <ul class="error-list">
        <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
      </ul>
      <template #footer>
        <AeButton variant="primary" @click="showErrorModal = false">{{ t('plotForm.ok') }}</AeButton>
      </template>
    </AeModal>

    <AeModal v-model="showOrchardModal" :title="t('plotForm.orchardModalTitle')">
      <PlotOrchardEditor v-model="orchardData" />
      <template #footer>
        <AeButton variant="primary" @click="showOrchardModal = false">{{ t('plotForm.confirm') }}</AeButton>
      </template>
    </AeModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { translateCropName } from '../i18n/cropVocab'
import { useDbStore } from '../stores/dbStore'
import { usePoderiStore } from '../stores/poderiStore'
import { AeAlert, AeInput, AeTextarea, AeButton, AeSelect, AeIcon, AePanelSplit, AeStepper, AeModal } from '@aevue/components'
import PlotMap from './PlotMap.vue'
import PlotOrchardEditor from './PlotOrchardEditor.vue'

const { t } = useI18n()

const props = defineProps({
  plotId: {
    type: [String, Number],
    default: null
  },
  farmCenterId: {
    type: [String, Number],
    required: true
  },
  entityType: {
    type: String,
    default: 'plot',
    validator: (v) => ['plot', 'geo_element'].includes(v)
  },
  centerLat: {
    type: Number,
    default: null
  },
  centerLon: {
    type: Number,
    default: null
  },
  otherPlots: {
    type: Array,
    default: () => []
  },
  seasonId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['submit', 'cancel'])

const dbStore = useDbStore()
const poderiStore = usePoderiStore()
const loading = ref(false)
const validationErrors = ref([])
const showErrorModal = ref(false)
const showOrchardModal = ref(false)
const orchardData = ref({})
const hasOrchardData = computed(() =>
  orchardData.value.planting_year || orchardData.value.cultivar || orchardData.value.dist_x
)
const selectedSeasonCrop = ref(null)
const relatedFields = ref([])
const relatedSeasons = ref([])
const stepperRef = ref(null)

const isEditMode = computed(() => !!props.plotId)

const selectedGeomType = computed(() => {
  if (props.entityType !== 'geo_element') return 'POLYGON'
  if (!formData.value.id_type) return 'POLYGON'
  const type = geoElementTypes.value.find(gt => gt.id_geo_element_type === formData.value.id_type)
  return type?.default_geom_type || 'POLYGON'
})

const stepGeometry = computed(() => ({
  key: 'geometry',
  label: t('plotForm.stepGeometryLabel'),
  description: t('plotForm.stepGeometryDescription'),
  validate: () => {
    const errs = []
    if (!formData.value.name?.trim()) {
      errs.push(t('plotForm.nameRequired'))
    }
    const coords = formData.value.polygon_coordinates
    const geomType = selectedGeomType.value
    const hasGeometry = !!plotGeometry.value
    if (!hasGeometry) {
      if (geomType === 'POINT') {
        if (!coords || coords.length < 1) errs.push(t('plotForm.pointRequired'))
      } else if (geomType === 'LINESTRING') {
        if (!coords || coords.length < 2) errs.push(t('plotForm.lineRequired'))
      } else {
        if (!coords || coords.length < 3) errs.push(t('plotForm.polygonRequired'))
      }
    }
    if (errs.length > 0) {
      validationErrors.value = errs
      nextTick(() => { showErrorModal.value = true })
      return false
    }
    validationErrors.value = []
    return true
  }
}))

const stepDetails = computed(() => ({
  key: 'details',
  label: t('plotForm.stepDetailsLabel'),
  description: t('plotForm.stepDetailsDescription'),
  validate: () => {
    const ok = validateForm()
    if (!ok) nextTick(() => { showErrorModal.value = true })
    return ok
  }
}))

const steps = computed(() => isEditMode.value ? [stepGeometry.value] : [stepGeometry.value, stepDetails.value])

const allCrops = ref([])
const allCropOptions = ref([])

// Carica tipi di elementi geografici
const geoElementTypes = ref([])
const geoElementTypeOptions = computed(() => {


  //escludi code=plot
  const filteredGeoElementTypes = geoElementTypes.value.filter(type => type.code !== 'plot')

  return filteredGeoElementTypes.map(type => ({
    value: type.id_geo_element_type,
    label: type.label || type.code
  }))
})

const formData = ref({
  name: '',
  plot_type: 'field',
  id_crop: null,
  id_type: null,
  area: 0,
  note: '',
  description: '',
  polygon_coordinates: [],
  geojson: null,
  id_farm_center: props.farmCenterId
})

const plotGeometry = ref(null)

const errors = ref({
  name: '',
  area: ''
})

function onCropChange(idCrop) {
  const crop = allCrops.value.find(c => c.id_crop === idCrop || c.id_crop == idCrop)
  if (!crop) return
  formData.value.plot_type = (crop.permanent === 1 || crop.permanent === true) ? 'orchard' : 'field'
  // per colture annuali, usare selectedSeasonCrop per la creazione del field
  if (formData.value.plot_type !== 'orchard') {
    selectedSeasonCrop.value = idCrop
  } else {
    selectedSeasonCrop.value = null
  }
}

onMounted(async () => {
  // Carica dati di lookup
  try {
    if (props.entityType === 'plot') {
      const cropRes = await dbStore.select('crop')
      allCrops.value = cropRes.ok ? cropRes.data || [] : []
      allCropOptions.value = await poderiStore.getCropOptions()
    } else {
      const typesResult = await dbStore.select('geo_element_type')
      if (typesResult.ok) {
        geoElementTypes.value = typesResult.data || []
      }
    }
  } catch (error) {
    console.error('Error loading lookup data:', error)
  }

  if (isEditMode.value) {
    loading.value = true
    try {
      const tableName = props.entityType === 'plot' ? 'plot' : 'geo_element'
      const result = await dbStore.selectOne(tableName, props.plotId)

      if (result.ok && result.data) {
        const item = result.data
        formData.value = {
          name: item.name || '',
          plot_type: item.plot_type || 'field',
          id_crop: item.id_crop || null,
          id_type: item.id_type || null,
          area: item.area || item.surface || 0,
          note: item.note || '',
          description: item.description || '',
          polygon_coordinates: item.polygon_coordinates || [],
          geojson: item.geojson || null,
          id_farm_center: props.farmCenterId
        }

        // Carica dati di impianto (campi extra espansi da json)
        orchardData.value = {
          cultivar:              item.cultivar || '',
          planting_year:         item.planting_year || null,
          vigour:                item.vigour || '',
          inerbimento_naturale:  item.inerbimento_naturale || '',
          dist_x:                item.dist_x || null,
          dist_y:                item.dist_y || null,
          density:               item.density || null,
        }

        // Load geometry into map
        if (item.geojson) {
          plotGeometry.value = typeof item.geojson === 'string'
            ? JSON.parse(item.geojson)
            : item.geojson
        } else if (item.polygon_coordinates && item.polygon_coordinates.length > 0) {
          plotGeometry.value = item.polygon_coordinates
        }

        // Carica i field collegati per mostrare warning
        if (props.entityType === 'plot') {
          try {
            const [fieldsResult, seasonsResult] = await Promise.all([
              poderiStore.fetchFieldsByFarmCenter(props.farmCenterId),
              dbStore.select('season', { id_farm_center: props.farmCenterId })
            ])
            relatedFields.value = fieldsResult.filter(f => f.id_plot === props.plotId)
            relatedSeasons.value = seasonsResult.ok ? seasonsResult.data || [] : []
          } catch (error) {
            console.error('Error loading related fields:', error)
          }
        }
      }
    } catch (error) {
      console.error('Error loading item:', error)
      const entityLabel = props.entityType === 'plot' ? t('plotForm.entityLabelPlot') : t('plotForm.entityLabelGeoElement')
      validationErrors.value = [t('plotForm.loadError', { entity: entityLabel })]
    } finally {
      loading.value = false
    }
  }
})

function handleGeometryUpdate(geometry) {
  if (geometry) {
    formData.value.polygon_coordinates = geometry.coordinates
    formData.value.geojson = geometry.geojson
  } else {
    formData.value.polygon_coordinates = []
    formData.value.geojson = null
  }
}

function handleAreaUpdate(area) {
  formData.value.area = area
}

function validateForm() {
  validationErrors.value = []
  errors.value = { name: '', area: '' }

  let isValid = true

  if (!formData.value.name?.trim()) {
    errors.value.name = t('plotForm.nameRequired')
    isValid = false
  }

  if (formData.value.area < 0) {
    errors.value.area = t('plotForm.areaNegative')
    isValid = false
  }

  // if (props.entityType === 'plot' && formData.value.plot_type === 'orchard' && !formData.value.id_crop) {
  //   validationErrors.value.push('Seleziona una coltura per il frutteto')
  //   isValid = false
  // }

  if (props.entityType === 'geo_element' && !formData.value.id_type) {
    validationErrors.value.push(t('plotForm.typeRequired'))
    isValid = false
  }

  return isValid
}

async function handleSubmit() {
  if (!validateForm()) return

  loading.value = true

  try {
    const tableName = props.entityType === 'plot' ? 'plot' : 'geo_element'

    const data = {
      name: formData.value.name.trim(),
      area: formData.value.area,
      polygon_coordinates: formData.value.polygon_coordinates || [],
      geojson: formData.value.geojson ?
        (typeof formData.value.geojson === 'string' ? formData.value.geojson : JSON.stringify(formData.value.geojson))
        : null,
      id_farm_center: props.farmCenterId
    }

    // Campi specifici per plot
    if (props.entityType === 'plot') {
      const isPermanent = formData.value.plot_type === 'orchard'
      data.plot_type = formData.value.plot_type
      data.id_crop = isPermanent ? formData.value.id_crop : null
      data.surface = formData.value.area
      data.note = formData.value.note?.trim() || ''
      // Dati di impianto (finiscono in plot.json via sync)
      if (isPermanent && hasOrchardData.value) {
        Object.assign(data, orchardData.value)
      }
    } else {
      data.id_type = formData.value.id_type
      data.description = formData.value.description?.trim() || ''
    }

    let result
    if (isEditMode.value) {
      result = await dbStore.update(tableName, props.plotId, data)
    } else {
      result = await dbStore.insert(tableName, data)
    }

    if (result.ok) {
      const cropForSeason = formData.value.plot_type === 'orchard' ? formData.value.id_crop : (selectedSeasonCrop.value || formData.value.id_crop)
      const newPlotId = result.data

      if (!isEditMode.value && props.seasonId && cropForSeason && newPlotId) {
        await poderiStore.createField({
          id_farm_center: props.farmCenterId,
          id_plot: newPlotId,
          id_season: props.seasonId,
          id_crop: cropForSeason
        })
      }

      emit('submit', result.data)
    } else {
      validationErrors.value = [result.message || t('plotForm.saveError')]
    }
  } catch (error) {
    console.error('Error saving plot:', error)
    validationErrors.value = [error.message || t('plotForm.saveError')]
  } finally {
    loading.value = false
  }
}

function handleCancel() {
  emit('cancel')
}

// Expose methods for parent modal footer
defineExpose({
  handleSubmit,
  handleCancel,
  loading,
  validationErrors,
  stepperRef
})
</script>

<style scoped>
.plot-form {
  display: flex;
  flex-direction: column;
}

form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Stepper più compatto dentro il modale */
.plot-form :deep(.stepper-header) {
  padding: 0 0.5rem;
  gap: 0.5rem;
}

.plot-form :deep(.step-number) {
  width: 26px;
  height: 26px;
  font-size: 0.78rem;
}

.plot-form :deep(.step-label) {
  font-size: 0.8rem;
}

.plot-form :deep(.stepper-content) {
  padding: 0.5rem 0 0 0;
}

.plot-form :deep(.ae-stepper) {
  gap: 0.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.warning-content p {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  line-height: 1.5;
}

.warning-content p:last-child {
  margin-bottom: 0;
}

.warning-content .helper-text {
  font-size: 0.82rem;
  color: #975a16;
}

.related-fields-list {
  margin: 4px 0 0 0;
  padding-left: 1.2em;
  font-size: 0.82rem;
  color: #975a16;
  line-height: 1.6;
}

.geometry-step {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fields-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.derived-type {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
  padding: 0.3rem 0.6rem;
  background: var(--card-bg, #f7fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
}

.validation-errors {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 0.75rem;
}

.error {
  color: var(--danger-color, #e53e3e);
  font-size: 0.8rem;
  margin: 0.25rem 0;
}

.error-list {
  margin: 0;
  padding-left: 1.25rem;
  color: var(--danger-color, #e53e3e);
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
}
</style>
